<template>
    <div ref="mapContainer" class="mapContainer" ></div>
</template>
<script lang="ts" setup>
    import { ref, onMounted, onUnmounted, watch, reactive, computed } from 'vue';
    import mapIcon from '@/assets/images/contact/map-icon.webp';
    import type { configParams, configResponse } from '~/types'
    import { useI18n } from 'vue-i18n' // 导入 useI18n
    const { t } = useI18n() // 获取当前 i18n 语言（如 zh-CN/en-US）
    const props = withDefaults(
        defineProps<{
            /** 背景图片地址 */
            way?: configResponse['pageValue']['way'];
        }>(),
        {
            way: () => ({}),
        }
    );
    
    console.log('props:', props)
    // 定义默认经纬度
    const DEFAULT_LONGITUDE = '116.397428'
    const DEFAULT_LATITUDE = '39.90923'
    
    // 提取经纬度的辅助函数
    const getCoordinates = (location?: string) => {
        
        // 确保 location 是字符串
        if (typeof location !== 'string') {
            return [DEFAULT_LONGITUDE, DEFAULT_LATITUDE];
        }

        // 移除空格并分割坐标
        const coordinates = location.replace(/\s+/g, '').split(',');
        
        // 确保分割后的数组长度为2
        if (coordinates.length !== 2) {
            return [DEFAULT_LONGITUDE, DEFAULT_LATITUDE];
        }
        
        // 提取经纬度并转换为数字
        const lng = parseFloat(coordinates[0]);
        const lat = parseFloat(coordinates[1]);
                
        // 验证经纬度是否为有效数字
        if (isNaN(lng) || isNaN(lat)) {
            return [DEFAULT_LONGITUDE, DEFAULT_LATITUDE];
        }
        
        // 验证经纬度是否在合理范围内
        const isValidLongitude = lng >= -180 && lng <= 180;
        const isValidLatitude = lat >= -90 && lat <= 90;
        
        if (!isValidLongitude || !isValidLatitude) {
            return [DEFAULT_LONGITUDE, DEFAULT_LATITUDE];
        }
        
        // 转换为字符串返回
        return [lng.toString(), lat.toString()];
    };

    const mapContainer = ref<HTMLDivElement | null>(null);
    const map = ref<any>(null);
    const infoWindow = ref<any>(null);
     
    const AMapKey = reactive({
        AMap_securityJsCode: '91f1c274813b8bef5105d27150f64d94', // 高德地图code
        AMap_key: '1dfae7be1b3f63c4f84953336e5bb8d1', // 高德地图key
    });
    
    // 使用 computed 属性计算 mapDate
    const mapDate = computed(() => {
        const coordinates = getCoordinates(props?.way?.location);
        
        return {
            position: coordinates,
            title: props?.way?.companyName,
            info: {
                title: props?.way?.companyName ,
                address: props?.way?.address ,
                phone: props?.way?.phone ,
                fax: props?.way?.fax 
            }
        };
    });
    
    // 根据屏幕宽度计算infoWindow的偏移量
    const getInfoWindowOffset = () => {
        // 默认偏移量
        let offsetY = -20;
        
        // 根据屏幕宽度调整偏移量
        if (window.innerWidth <= 460) {
            // 小屏幕设备，增加向上的偏移量
            offsetY = -5;
        } else if (window.innerWidth <= 768) {
            // 中等屏幕设备，略微调整偏移量
            offsetY = -15;
        }
        
        return new AMap.Pixel(0, offsetY);
    };
    
    // 初始化地图
    const initMap = async () => {
        if (typeof window === 'undefined') {
            return;
        }

        if (!mapContainer.value) {
            return;
        }

        // 确保容器有尺寸
        if (mapContainer.value.clientWidth === 0 || mapContainer.value.clientHeight === 0) {
            return;
        }

        try {
            window._AMapSecurityConfig = {
                securityJsCode: AMapKey.AMap_securityJsCode,
            };
            
            const AMapLoader = await import('@amap/amap-jsapi-loader');
            
            const AMap = await AMapLoader.load({
                key: AMapKey.AMap_key,
                version: "2.0",
                plugins: ["AMap.ToolBar", "AMap.Scale"],
            });

            map.value = new AMap.Map(mapContainer.value, {
                zoom: 16,
                center: [
                    mapDate.value.position[0],
                    mapDate.value.position[1]
                ],
                viewMode: "2D",
                mapStyle: "amap://styles/grey",
                resizeEnable: true,
            });

            const marker = new AMap.Marker({
                position: mapDate.value.position,
                icon: mapIcon,
                iconAnchor: 'bottom-center',
                size: new AMap.Size(82, 82),
                offset: new AMap.Pixel(-41, 0),
            });
            map.value.add(marker);

            const infoWindowContent = ` <div class="map-info">
                                            <h3 class="map-title font16 color-f">${mapDate.value.info.title}</h3>
                                            <div class="map-text font16 color-f">
                                                <p>${t('contact.address')}：${mapDate.value.info.address}</p>
                                                <p>${t('contact.phone')}：${mapDate.value.info.phone}</p>
                                                <p>${t('contact.fax')}：${mapDate.value.info.fax}</p>
                                            </div>
                                        </div>`;

            infoWindow.value = new AMap.InfoWindow({
                content: infoWindowContent,
                // offset: new AMap.Pixel(0, -60), // 调整信息窗口位置，避免遮挡标记点
                offset: getInfoWindowOffset(), // 使用动态计算的偏移量
            });

            infoWindow.value.open(map.value, mapDate.value.position);

        } catch (e) {
        }
    };

    // 窗口大小变化时重新调整地图大小
    const handleResize = () => {
        if (map.value) {
            console.log('Resizing map...');
            map.value.resize();

            // 如果infoWindow已打开，更新其位置
            if (infoWindow.value && mapDate.value) {
                infoWindow.value.setOffset(getInfoWindowOffset());
                infoWindow.value.open(map.value, mapDate.value.position);
            }
        }
    };

    onMounted(() => {
        initMap();
        window.addEventListener('resize', handleResize);
    });
    
    onUnmounted(() => {
        if (map.value) {
            map.value.destroy();
            map.value = null;
        }
        window.removeEventListener('resize', handleResize);
    });
    
    // 监听 way 属性变化，更新地图
    watch(() => props.way, (newWay) => {
        console.log('way changed:', newWay);
        console.log('newWay.location:', newWay?.location);
        // 重新初始化地图
        if (map.value) {
            map.value.destroy();
            map.value = null;
        }
        initMap();
    }, { deep: true });
</script>
<style scoped lang="scss">
    .mapContainer {
        position: relative;
        width: 100%;
        height: 100%; /* 添加默认高度 */
        background-color: var(--main-color) !important;
        overflow: hidden;
    }
    :deep(.amap-icon){
        img{
            width: 100%;
            height: 100%;
        }
    }
    :deep(.amap-info-content){
        width: 400px;
        padding: 24px;
        border-radius: 14px;
        background-color: var(--main-color);
        .amap-info-close{
            display: none;
        }
        .map-title{
            line-height: 1.625;
            margin-bottom: 12px;
            font-weight: normal;
        }
        .map-text{
            line-height: 1.625;
            opacity: .5;
        }
    }
    :deep(.amap-info-sharp){
        border-top: 8px solid var(--main-color);
    }
    
    @media (max-width:460px){
        
        :deep(.amap-info-content){
            width: 300px;
        }
    }
</style>